// Add color validation functions
function isColorTooLight(r: number, g: number, b: number): boolean {
  // Calculate relative luminance using the formula: (0.299*R + 0.587*G + 0.114*B)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.85 // If luminance is > 85%, color is too light
}

function isColorTooGrey(r: number, g: number, b: number): boolean {
  // Check if the difference between colors is too small (indicating greyness)
  const tolerance = 30
  return Math.abs(r - g) < tolerance
    && Math.abs(g - b) < tolerance
    && Math.abs(r - b) < tolerance
}

function isColorGrey(r: number, g: number, b: number): boolean {
  // Check if the color is a shade of grey (all components nearly equal)
  const tolerance = 5
  return Math.abs(r - g) < tolerance
    && Math.abs(g - b) < tolerance
    && Math.abs(r - b) < tolerance
}

function isWhite(r: number, g: number, b: number): boolean {
  const tolerance = 10 // Allow slight variation from pure white
  return r > (255 - tolerance)
    && g > (255 - tolerance)
    && b > (255 - tolerance)
}

export async function getDominantColor(imageUrl: string): Promise<string> {
  try {
    const proxyUrl = `/api/proxy/image?url=${encodeURIComponent(imageUrl)}`

    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    if (!context) return null

    const img = new Image()

    return new Promise((resolve) => {
      img.onload = () => {
        canvas.width = 50
        canvas.height = 50
        context.drawImage(img, 0, 0, 50, 50)

        const pixels = context.getImageData(0, 0, 50, 50).data
        const colorCounts: { [key: string]: number } = {}

        // Count all colors except white
        for (let i = 0; i < pixels.length; i += 16) {
          const r = pixels[i]
          const g = pixels[i + 1]
          const b = pixels[i + 2]
          const a = pixels[i + 3]

          // Skip transparent and white pixels
          if (a < 127 || isWhite(r, g, b)) continue

          const key = `${r},${g},${b}`
          colorCounts[key] = (colorCounts[key] || 0) + 1
        }

        // Find the most common color
        const dominantColor = Object.entries(colorCounts)
          .sort(([,a], [,b]) => b - a)[0]?.[0]

        if (dominantColor) {
          const [r, g, b] = dominantColor.split(',').map(Number)
          resolve(`rgb(${r}, ${g}, ${b})`)
        } else {
          resolve('#5F0CE2') // Default if no color found
        }
      }

      img.onerror = () => {
        console.error('Error loading image:', proxyUrl)
        resolve('#5F0CE2')
      }

      img.src = proxyUrl
    })
  } catch (error) {
    console.error('Error getting dominant color:', error)
    return '#5F0CE2'
  }
}
